import React, { useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';

interface Hero3DSectionProps {
  content: any;
  language: 'en' | 'mr';
  onCelebrate: () => void;
}

export const Hero3DSection: React.FC<Hero3DSectionProps> = ({ content, language, onCelebrate }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || !photoRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;

      photoRef.current.style.transform = `
        perspective(1000px)
        rotateX(${y * 10}deg)
        rotateY(${x * 10}deg)
        scale(1.05)
      `;
    };

    const handleMouseLeave = () => {
      if (photoRef.current) {
        photoRef.current.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale(1)';
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const title = language === 'mr' ? content.birthday.titleMr : content.birthday.title;
  const name = language === 'mr' ? content.birthday.nameMr : content.birthday.name;
  const description = language === 'mr' ? content.event.descriptionMr : content.event.description;

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden" ref={containerRef}>
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-pink-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute bottom-20 left-1/3 w-96 h-96 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
      </div>

      <div className="relative z-10 max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        {/* Photo - 3D Transform */}
        <div className="flex justify-center perspective">
          <div className="relative w-full aspect-square max-w-md">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-pink-500 rounded-3xl blur-2xl opacity-75 animate-pulse" />
            <img
              ref={photoRef}
              src={content.birthday.photo}
              alt={content.birthday.name}
              className="relative w-full h-full object-cover rounded-3xl shadow-2xl transition-transform duration-300 border-4 border-cyan-400"
              onError={(e) => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=500';
              }}
            />
          </div>
        </div>

        {/* Text Content */}
        <div className="flex flex-col gap-6">
          <div className="space-y-4">
            <h2 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 animate-pulse">
              {title}
            </h2>
            {content.birthday.age !== undefined && (
              <h1 className="text-6xl md:text-8xl font-black text-white drop-shadow-lg">
                {content.birthday.age}
              </h1>
            )}
            <p className="text-3xl md:text-4xl font-bold text-pink-300">
              {name}
            </p>
          </div>

          <p className="text-lg text-gray-300 leading-relaxed">
            {description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 pt-6">
            <button
              onClick={onCelebrate}
              className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-110"
            >
              <span className="flex items-center gap-2">
                <Sparkles size={20} />
                Celebrate
              </span>
            </button>
            <button className="px-8 py-4 bg-pink-600 text-white font-bold rounded-full hover:shadow-lg hover:shadow-pink-500/50 transition-all transform hover:scale-110">
              Join Party
            </button>
          </div>

          {/* Event Info Badges */}
          <div className="flex flex-wrap gap-3 pt-4">
            <div className="px-4 py-2 bg-cyan-900/50 border border-cyan-400 rounded-full text-sm text-cyan-300 font-semibold">
              📅 {language === 'mr' ? content.event.dateMr : content.event.dateStr}
            </div>
            <div className="px-4 py-2 bg-pink-900/50 border border-pink-400 rounded-full text-sm text-pink-300 font-semibold">
              🕐 {content.event.time}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
        <div className="w-6 h-10 border-2 border-cyan-400 rounded-full flex justify-center">
          <div className="w-1 h-2 bg-cyan-400 rounded-full mt-2 animate-bounce" />
        </div>
      </div>
    </section>
  );
};
